import {
  ArrowRight,
  Clock,
  Download,
  Share2,
  ShieldAlert,
  ShieldCheck,
  TrendingUp,
  Upload,
  Wallet,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { formatCurrency, formatPercent } from "../shared/currency";
import type { DashboardPeriod, OperationalQueues } from "../trips/selectors";
import type {
  BackupMeta,
  DashboardMetrics,
  DataProtectionState,
} from "../trips/types";

type DashboardPageProps = {
  period: DashboardPeriod;
  onPeriodChange: (period: DashboardPeriod) => void;
  periodMetrics: DashboardMetrics;
  metrics: DashboardMetrics;
  activeTripsCount: number;
  exceptionsCount: number;
  queues: OperationalQueues;
  dataProtectionState: DataProtectionState;
  backupMeta: BackupMeta;
  hasBusinessData: boolean;
  isDataBusy: boolean;
  onOpenDesk: () => void;
  onProtectData: () => void;
  onBackupData: () => void;
  onShareBackup: () => void;
  onRestoreData: () => void;
};

type DashboardScope = DashboardPeriod | "lifetime";

export function DashboardPage({
  period,
  onPeriodChange,
  periodMetrics,
  metrics,
  activeTripsCount,
  exceptionsCount,
  queues,
  dataProtectionState,
  backupMeta,
  hasBusinessData,
  isDataBusy,
  onOpenDesk,
  onProtectData,
  onBackupData,
  onShareBackup,
  onRestoreData,
}: DashboardPageProps) {
  const defaultScope = getDefaultDashboardScope(period, periodMetrics, metrics);
  const [scope, setScope] = useState<DashboardScope>(defaultScope);
  const [hasUserSelectedScope, setHasUserSelectedScope] = useState(false);
  const activeMetrics = scope === "lifetime" ? metrics : periodMetrics;
  const marginPct =
    activeMetrics.totalHire > 0
      ? activeMetrics.totalProfit / activeMetrics.totalHire
      : 0;
  const collectionPct =
    activeMetrics.totalHire > 0
      ? (activeMetrics.totalHire - activeMetrics.totalOutstanding) /
        activeMetrics.totalHire
      : 0;
  const expensePct =
    activeMetrics.totalHire > 0
      ? activeMetrics.totalExpense / activeMetrics.totalHire
      : 0;
  const attentionCount =
    exceptionsCount +
    activeTripsCount +
    queues.deliveredNotBilled.length +
    queues.overdueReceivables.length;

  useEffect(() => {
    if (!hasUserSelectedScope) setScope(defaultScope);
  }, [defaultScope, hasUserSelectedScope]);

  const selectScope = (nextScope: DashboardScope) => {
    setHasUserSelectedScope(true);
    setScope(nextScope);
    if (nextScope !== "lifetime") onPeriodChange(nextScope);
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      <section className="orbit-card p-3 sm:p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-semibold text-slate-950">Dashboard</h2>
            <p className="hidden text-sm text-slate-500 sm:block">
              Financial pulse and the next place that needs attention.
            </p>
          </div>
          <div className="orbit-raised flex shrink-0 p-1">
            {(["today", "week", "lifetime"] as DashboardScope[]).map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => selectScope(item)}
                className={`whitespace-nowrap rounded-lg px-3 py-2 text-xs font-semibold sm:text-sm ${
                  scope === item ? "orbit-tab-active" : "orbit-tab"
                }`}
              >
                {item === "today"
                  ? "Today"
                  : item === "week"
                    ? "This Week"
                    : "Lifetime"}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-[160px_minmax(0,1fr)_280px] lg:items-stretch">
          <PulseRing
            value={marginPct}
            amount={formatCurrency(activeMetrics.totalProfit)}
            detail={`${activeMetrics.lowMarginTrips} low-margin trips`}
          />
          <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-3 lg:grid-cols-1">
            <FinancialLine
              label="Revenue"
              value={formatCurrency(activeMetrics.totalHire)}
              percent={activeMetrics.totalHire > 0 ? 1 : 0}
              icon={<TrendingUp size={16} />}
              tone="orbit-tone-blue"
            />
            <FinancialLine
              label="Expense"
              value={formatCurrency(activeMetrics.totalExpense)}
              percent={expensePct}
              icon={<Wallet size={16} />}
              tone="orbit-tone-rose"
            />
            <FinancialLine
              label="Outstanding"
              value={formatCurrency(activeMetrics.totalOutstanding)}
              percent={1 - collectionPct}
              icon={<Clock size={16} />}
              tone="orbit-tone-amber"
            />
          </div>
          <div className="orbit-raised flex min-w-0 flex-col justify-between p-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                Work needs attention
              </p>
              <p className="orbit-display mt-2 text-3xl font-bold text-slate-950">
                {attentionCount}
              </p>
              <p className="mt-1 text-sm text-slate-500">
                Queue items across movement, billing, collections, and data quality.
              </p>
            </div>
            <button
              type="button"
              onClick={onOpenDesk}
              className="mt-3 inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-700"
            >
              Open Desk
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      <DataSafetyPanel
        protectionState={dataProtectionState}
        backupMeta={backupMeta}
        hasBusinessData={hasBusinessData}
        isBusy={isDataBusy}
        onProtect={onProtectData}
        onBackup={onBackupData}
        onShare={onShareBackup}
        onRestore={onRestoreData}
      />
    </div>
  );
}

function PulseRing({ value, amount, detail }: { value: number; amount: string; detail: string }) {
  const percent = clamp01(value);
  return (
    <div className="flex min-w-0 items-center justify-center gap-3 text-center lg:flex-col">
      <div
        className="grid h-28 w-28 shrink-0 place-items-center rounded-full"
        style={{
          background: `conic-gradient(var(--orbit-green) ${percent * 360}deg, var(--orbit-slate-dim) 0deg)`,
        }}
      >
        <div className="grid h-[88px] w-[88px] place-items-center rounded-full bg-white text-center">
          <div>
            <p className="orbit-display text-xl font-bold text-slate-950">
              {formatPercent(percent)}
            </p>
            <p className="text-[10px] font-bold uppercase text-slate-500">Margin</p>
          </div>
        </div>
      </div>
      <div className="min-w-0 text-left lg:text-center">
        <p className="orbit-display truncate text-xl font-bold text-slate-950">{amount}</p>
        <p className="text-xs text-slate-500">{detail}</p>
      </div>
    </div>
  );
}

function FinancialLine({
  label,
  value,
  percent,
  icon,
  tone,
}: {
  label: string;
  value: string;
  percent: number;
  icon: ReactNode;
  tone: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className={`${tone} shrink-0 rounded-lg p-1.5`}>{icon}</span>
          <p className="truncate text-sm font-semibold text-slate-950">{label}</p>
        </div>
        <p className="orbit-display shrink-0 text-sm font-bold text-slate-950">{value}</p>
      </div>
      <div className="mt-2 h-1 overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-amber-500"
          style={{ width: `${clamp01(percent) * 100}%` }}
        />
      </div>
    </div>
  );
}

function DataSafetyPanel({
  protectionState,
  backupMeta,
  hasBusinessData,
  isBusy,
  onProtect,
  onBackup,
  onShare,
  onRestore,
}: {
  protectionState: DataProtectionState;
  backupMeta: BackupMeta;
  hasBusinessData: boolean;
  isBusy: boolean;
  onProtect: () => void;
  onBackup: () => void;
  onShare: () => void;
  onRestore: () => void;
}) {
  const changedAfterBackup = Boolean(
    backupMeta.lastDataChangeAt &&
      (!backupMeta.lastBackupAt || backupMeta.lastDataChangeAt > backupMeta.lastBackupAt)
  );
  const backupAge = backupMeta.lastBackupAt
    ? Date.now() - new Date(backupMeta.lastBackupAt).getTime()
    : Number.POSITIVE_INFINITY;
  const backupDue =
    hasBusinessData &&
    (!backupMeta.lastBackupAt ||
      (changedAfterBackup && backupAge >= 7 * 24 * 60 * 60 * 1000));
  const protectedOnDevice = protectionState === "protected";
  const ProtectionIcon = protectedOnDevice ? ShieldCheck : ShieldAlert;

  return (
    <section
      id="data-safety"
      tabIndex={-1}
      className={`orbit-card p-3 sm:p-4 ${backupDue ? "orbit-data-safety-due" : ""}`}
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <div
            className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${
              protectedOnDevice ? "orbit-tone-emerald" : "orbit-tone-amber"
            }`}
          >
            <ProtectionIcon size={18} />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-semibold text-slate-950">Data Safety</h2>
              {backupDue ? (
                <span className="orbit-tone-amber rounded-full px-2 py-0.5 text-xs font-bold">
                  Backup due
                </span>
              ) : null}
            </div>
            <p className="mt-0.5 text-xs text-slate-500 sm:text-sm">
              {protectionState === "protected"
                ? "Device storage protected"
                : protectionState === "unsupported"
                  ? "Protection status unavailable"
                  : "Device cleanup can remove local records"}
              {` · Last backup ${formatBackupDate(backupMeta.lastBackupAt)}`}
              {changedAfterBackup ? " · New changes pending" : ""}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap lg:shrink-0 lg:flex-nowrap lg:justify-end">
          {protectionState === "unprotected" ? (
            <DataAction icon={<ShieldCheck size={15} />} label="Protect" onClick={onProtect} disabled={isBusy} />
          ) : null}
          <button
            type="button"
            onClick={onBackup}
            disabled={isBusy}
            className="orbit-button-primary min-h-11 px-3 py-2 text-sm"
          >
            <Download size={15} />
            Back Up
          </button>
          <DataAction icon={<Share2 size={15} />} label="Share" onClick={onShare} disabled={isBusy} />
          <DataAction icon={<Upload size={15} />} label="Restore" onClick={onRestore} disabled={isBusy} />
        </div>
      </div>
    </section>
  );
}

function DataAction({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-700 disabled:opacity-45"
    >
      {icon}
      {label}
    </button>
  );
}

const formatBackupDate = (value?: string): string => {
  if (!value) return "never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown";
  return date.toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const hasMoneyData = (metrics: DashboardMetrics): boolean =>
  metrics.totalHire > 0 ||
  metrics.totalExpense > 0 ||
  metrics.totalOutstanding > 0 ||
  metrics.totalProfit !== 0;

const getDefaultDashboardScope = (
  period: DashboardPeriod,
  periodMetrics: DashboardMetrics,
  metrics: DashboardMetrics
): DashboardScope =>
  !hasMoneyData(periodMetrics) && hasMoneyData(metrics) ? "lifetime" : period;

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));
